import { computed, inject } from '@angular/core';
import { AuthApi } from '@app/api/auth.api';
import { UserApi } from '@app/api/user.api';
import {
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
  UserDto,
} from '@app/models';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, finalize, pipe, switchMap, tap } from 'rxjs';

interface UserState {
  user: UserDto | null;
  isLoading: boolean;
  isResetPasswordLoading: boolean;
  isResetPasswordRequestLoading: boolean;
  isInitialized: boolean;
}

const initialState: UserState = {
  user: null,
  isLoading: false,
  isResetPasswordLoading: false,
  isResetPasswordRequestLoading: false,
  isInitialized: false,
};

export const UserStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    isAuthenticated: computed(() => !!store.user()),
  })),

  withMethods((store) => {
    const userApi = inject(UserApi);
    const authApi = inject(AuthApi);

    const loadUser = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(() =>
          userApi.getMyselfDetails$().pipe(
            tap((user) =>
              patchState(store, {
                user,
                isLoading: false,
                isInitialized: true,
              }),
            ),
            catchError(() => {
              patchState(store, { user: null, isInitialized: true });

              return EMPTY;
            }),
            finalize(() => patchState(store, { isLoading: false })),
          ),
        ),
      ),
    );

    const register = rxMethod<{
      request: RegisterRequest;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(({ request, onSuccess }) =>
          authApi.registerUser$(request).pipe(
            tap(() => {
              patchState(store, { isLoading: false });

              onSuccess();
            }),
          ),
        ),
      ),
    );

    const resetPassword = rxMethod<{
      request: ResetPasswordRequest;
      onError: () => void;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isResetPasswordLoading: true })),
        switchMap(({ request, onError, onSubmit, onSuccess }) =>
          authApi.resetPassword(request).pipe(
            catchError(() => {
              patchState(store, { isResetPasswordLoading: false });

              onError();

              return EMPTY;
            }),
            tap(() => {
              patchState(store, { isResetPasswordLoading: false });

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const activate = rxMethod<{
      request: ResetPasswordRequest;
      onError: () => void;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isResetPasswordLoading: true })),
        switchMap(({ request, onError, onSubmit, onSuccess }) =>
          authApi.activateUser(request).pipe(
            catchError(() => {
              patchState(store, { isResetPasswordLoading: false });

              onError();

              return EMPTY;
            }),
            tap(() => {
              patchState(store, { isResetPasswordLoading: false });

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const resetPasswordRequest = rxMethod<{
      email: string;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isResetPasswordRequestLoading: true })),
        switchMap(({ email, onSubmit, onSuccess }) =>
          authApi.resetPasswordRequest(email).pipe(
            catchError(() => {
              patchState(store, { isResetPasswordLoading: false });

              onSubmit();

              return EMPTY;
            }),
            tap(() => {
              patchState(store, { isResetPasswordRequestLoading: false });

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const login = rxMethod<{
      request: LoginRequest;
      onError: () => void;
      onSubmit: (response: UserDto) => void;
    }>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(({ request, onError, onSubmit }) =>
          authApi.signIn$(request).pipe(
            catchError(() => {
              patchState(store, { user: null, isLoading: false });

              onError();

              return EMPTY;
            }),
            tap((user) => {
              onSubmit(user);

              patchState(store, {
                user,
                isLoading: false,
                isInitialized: true,
              });
            }),
          ),
        ),
      ),
    );

    const logout = rxMethod<{ onSuccess: () => void }>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(({ onSuccess }) =>
          authApi.logout().pipe(
            tap(() => {
              patchState(store, { user: null, isLoading: false });

              onSuccess();
            }),
          ),
        ),
      ),
    );

    const setUser = (user: UserDto | null) =>
      patchState(store, { user, isLoading: false });

    const clearUser = () => patchState(store, { user: null, isLoading: false });

    return {
      loadUser,
      register,
      resetPassword,
      activate,
      resetPasswordRequest,
      login,
      logout,
      setUser,
      clearUser,
    };
  }),
);
